import { Readable, Writable } from 'node:stream';
import {
  agent,
  methods,
  ndJsonStream,
  PROTOCOL_VERSION,
  RequestError,
  type AgentApp,
  type AgentContext,
  type AuthMethod,
  type InitializeResponse,
  type NewSessionResponse,
} from '@agentclientprotocol/sdk';
import { Logger, withPrefix } from '@gitlab-org/logging';
import { doNotAwait } from '@gitlab-org/core';
import { Service, ServiceLifetime } from '@gitlab/needle';
import { RuntimeContext } from '../../runtime_context';
import { SlashCommandService } from '../../slash_commands';
import { AcpInitializationService } from './acp_initialization_service';
import { AcpSessionService } from './acp_session_service';
import { AcpPromptRunner } from './acp_prompt_runner';
import { buildAvailableCommands } from './translation/available_commands';

const AUTH_METHOD_LOCAL_CREDENTIALS = 'local-credentials';

const AUTH_INSTRUCTIONS =
  'Run `glab auth login` in a terminal, or set the GITLAB_TOKEN environment variable, then retry.';

/**
 * The credential entry itself happens outside the protocol (glab login or an
 * env var in the agent-server config), so this method's description is the
 * UX: it tells the user what to do, and `authenticate` simply re-probes for
 * credentials afterwards.
 */
const AUTH_METHODS: AuthMethod[] = [
  {
    id: AUTH_METHOD_LOCAL_CREDENTIALS,
    name: 'Use local GitLab credentials',
    description: AUTH_INSTRUCTIONS,
  },
];

/**
 * Serves GitLab Duo as an ACP agent over stdio.
 */
@Service({
  dependencies: [
    Logger,
    RuntimeContext,
    AcpInitializationService,
    AcpSessionService,
    AcpPromptRunner,
    SlashCommandService,
  ],
  lifetime: ServiceLifetime.Singleton,
})
export class AcpServer {
  #logger: Logger;

  #runtimeContext: RuntimeContext;

  #initializationService: AcpInitializationService;

  #sessionService: AcpSessionService;

  #promptRunner: AcpPromptRunner;

  #slashCommandService: SlashCommandService;

  constructor(
    logger: Logger,
    runtimeContext: RuntimeContext,
    initializationService: AcpInitializationService,
    sessionService: AcpSessionService,
    promptRunner: AcpPromptRunner,
    slashCommandService: SlashCommandService,
  ) {
    this.#logger = withPrefix(logger, '[AcpServer]');
    this.#runtimeContext = runtimeContext;
    this.#initializationService = initializationService;
    this.#sessionService = sessionService;
    this.#promptRunner = promptRunner;
    this.#slashCommandService = slashCommandService;
  }

  /** Builds the ACP agent app. Public so tests can drive it via `client().connectWith(...)`. */
  buildApp(): AgentApp {
    return agent()
      .onRequest(methods.agent.initialize, () => this.#buildInitializeResponse())
      .onRequest(methods.agent.authenticate, async ({ params }) => {
        if (params.methodId !== AUTH_METHOD_LOCAL_CREDENTIALS) {
          throw RequestError.invalidParams(undefined, `Unknown auth method: ${params.methodId}`);
        }
        // Credentials are provided out-of-band (glab login / env var); this
        // re-probes for them and initializes the backend when they exist.
        await this.#requireAuth();
        this.#logger.info('authenticated');
        return {};
      })
      .onRequest(methods.agent.session.new, async ({ params, client }) => {
        await this.#requireAuth();
        const { sessionId } = await this.#sessionService.createSession(params.cwd);
        this.#announceAvailableCommands(sessionId, client);
        return { sessionId } satisfies NewSessionResponse;
      })
      .onRequest(methods.agent.session.prompt, async ({ params, client }) => {
        await this.#requireAuth();
        const state = this.#sessionService.requireState(params.sessionId);
        return this.#promptRunner.run(params, client, state);
      })
      .onNotification(methods.agent.session.cancel, ({ params }) => {
        this.#sessionService.cancel(params.sessionId);
      });
  }

  /**
   * Throws auth_required unless the backend is (or becomes) initialized. The
   * instructions ride in the error message because some clients (Zed) render
   * the failure as a plain banner rather than an authenticate prompt.
   */
  async #requireAuth(): Promise<void> {
    // A critical init failure rejects here (initialize() throws); rethrow it as
    // a well-formed JSON-RPC error rather than letting an SDK-dependent error
    // propagate uncaught out of the request handler.
    let authenticated: boolean;
    try {
      authenticated = await this.#initializationService.ensureInitialized();
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      throw RequestError.internalError(undefined, message);
    }
    if (!authenticated) {
      throw RequestError.authRequired(undefined, AUTH_INSTRUCTIONS);
    }
  }

  /**
   * Notifies the client of the commands available for this freshly created
   * session. Deferred a tick so the notification reaches the client after
   * the `session/new` response that carries the sessionId; sending it
   * inside the request handler would put it on the wire before the client
   * knows the session exists.
   *
   * We don't re-check the session still exists when the deferred tick fires:
   * stdio serializes requests and only one session is active at a time, so at
   * worst a superseding `session/new` leaves a harmless extra notification for
   * an evicted sessionId. It never announces the wrong command set.
   */
  #announceAvailableCommands(sessionId: string, client: AgentContext): void {
    setImmediate(() =>
      doNotAwait(
        client
          .notify(methods.client.session.update, {
            sessionId,
            update: {
              sessionUpdate: 'available_commands_update',
              availableCommands: buildAvailableCommands(
                this.#slashCommandService.getHeadlessCommands(),
              ),
            },
          })
          .catch((error) => {
            this.#logger.warn('failed to announce available commands', error);
          }),
      ),
    );
  }

  async serve(): Promise<void> {
    // Eagerly initialize when credentials are already available, so the
    // authenticated path surfaces critical errors at startup rather than on
    // the first session. Best-effort only: a startup failure here must not
    // kill the agent before the ACP handshake — the request handlers re-probe
    // via #requireAuth and resurface the error to the client properly.
    try {
      await this.#initializationService.ensureInitialized();
    } catch (e) {
      this.#logger.warn(
        'eager initialization failed; continuing unauthenticated',
        e instanceof Error ? e : new Error(String(e)),
      );
    }

    const app = this.buildApp();

    const stream = ndJsonStream(
      Writable.toWeb(process.stdout) as WritableStream<Uint8Array>,
      Readable.toWeb(process.stdin) as ReadableStream<Uint8Array>,
    );

    const connection = app.connect(stream);
    this.#logger.info('connected, waiting for requests...');

    await connection.closed;
    this.#logger.info('connection closed');
  }

  #buildInitializeResponse(): InitializeResponse {
    return {
      protocolVersion: PROTOCOL_VERSION,
      agentCapabilities: {
        loadSession: false,
        promptCapabilities: {
          image: false,
          audio: false,
          embeddedContext: false,
        },
      },
      agentInfo: {
        name: 'gitlab-duo',
        title: 'GitLab Duo',
        version: this.#runtimeContext.cliVersion,
      },
      authMethods: AUTH_METHODS,
    };
  }
}
